import React, { useEffect, useState } from "react";
import {
  Checkbox,
  Spacing,
  Button,
  MaskedInput,
  Loader,
  toast,
} from "@sicredi/react";
import DescriptionImage from "../../assets/description-picture.png";
import Check from "../../assets/check.svg";
import CheckMobile from "../../assets/check_mobile.svg";

import "./index.scss";
import { CheckResponse, FormErrors } from "./models";
import { isCMC7Valid, isCnpjValid, isCpfValid } from "./utils";
import { ResponseSection } from "./ResponseSection";
import { CheckLegalApi } from "#/api";
import { APPLICATION } from "#/constants";
import Header from "#/components/Header";
import Footer from "#/components/Footer";

export const Home: React.FC = () => {
  const [checkSuid, setCheckSuid] = useState<string>("");
  const [userSuid, setUserSuid] = useState<string>("");
  const [cmc7, setCmc7] = useState<string>("");
  const [terms, setTerms] = useState<boolean>(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [check, setCheck] = useState<CheckResponse>();
  const [load, setLoad] = useState<boolean>(false);
  const [isRecaptchaLoading, setIsRecaptchaLoading] = useState(true);
  const [token, setToken] = useState<string>();

  const CMC7_NOT_SICREDI_ERROR = "Digite um CMC7 de um cheque do Sicredi";

  const CMC7_MASK = "99999999 9999999999 999999999999"
    .split("")
    .map((char) => (char === "9" ? /\d/ : char));

  useEffect(() => {
    const scriptTag = document.createElement("script");
    scriptTag.src = "https://www.google.com/recaptcha/enterprise.js?render=explicit";
    scriptTag.async = true;
    scriptTag.addEventListener('load', () => setIsRecaptchaLoading(false))
    document.body.appendChild(scriptTag)
    console.log("Sicredi flow")
  }, [])

  useEffect(() => {
    if (isRecaptchaLoading) return;
    window.grecaptcha.enterprise.ready(() => {
      if (!window.grecaptcha.enterprise.render) {
        setIsRecaptchaLoading(false);
        return;
      }
      window.grecaptcha.enterprise.render("g-recaptcha", {
        sitekey: APPLICATION.RECAPTCHA_KEY,
        callback: (token: string) => {
          setToken(token);
        },
      });
      setIsRecaptchaLoading(false);
    });
  }, [isRecaptchaLoading]);

  useEffect(() => {
    if (cmc7.length >= 3 && cmc7.slice(0, 3) !== "748")
      setErrors((prev) => {
        return { ...prev, cmcSeven: CMC7_NOT_SICREDI_ERROR };
      });
    else if (errors.cmcSeven && errors.cmcSeven === CMC7_NOT_SICREDI_ERROR)
      setErrors((prev) => {
        return { ...prev, cmcSeven: "" };
      });
  }, [errors.cmcSeven, cmc7]);

  const validateForm = (): FormErrors => {
    const newErrors: FormErrors = {};

    const unmaskedCheckSuid = MaskedInput.unmask(checkSuid);
    const unmaskedUserSuid = MaskedInput.unmask(userSuid);

    if (![11, 14].includes(unmaskedCheckSuid.length))
      newErrors.checkSuid = "Informe o CPF/CNPJ impressos na folha do cheque";
    else if (
      (!isCpfValid(unmaskedCheckSuid) && unmaskedCheckSuid.length === 11) ||
      (!isCnpjValid(unmaskedCheckSuid) && unmaskedCheckSuid.length === 14)
    )
      newErrors.checkSuid = "Informe um CPF/CNPJ válido";

    if (![11, 14].includes(unmaskedUserSuid.length))
      newErrors.userSuid = "Informe um número de CPF/CNPJ ";
    else if (
      (!isCpfValid(unmaskedUserSuid) && unmaskedUserSuid.length === 11) ||
      (!isCnpjValid(unmaskedUserSuid) && unmaskedUserSuid.length === 14)
    )
      newErrors.userSuid = "Informe um CPF/CNPJ válido";

    if (cmc7.length >= 3 && cmc7.slice(0, 3) !== "748")
      newErrors.cmcSeven = CMC7_NOT_SICREDI_ERROR;
    else if (MaskedInput.unmask(cmc7).length < 30)
      newErrors.cmcSeven = "Preencha um número de CMC7";
    else if (!isCMC7Valid(MaskedInput.unmask(cmc7)))
      newErrors.cmcSeven = "Revise o número de CMC7";

    if (!newErrors.terms && !terms) newErrors.terms = "Leia e clique no campo";

    return newErrors;
  };

  const clearFields = () => {
    setCheckSuid("");
    setUserSuid("");
    setCmc7("");
    setTerms(false);
    setErrors({});
  };

  const getCheckInformation = async () => {
    const hasErrors = validateForm();
    if (
      hasErrors.checkSuid ||
      hasErrors.cmcSeven ||
      hasErrors.terms ||
      hasErrors.userSuid
    ) {
      setErrors(hasErrors);
      return;
    } else if (!token) {
      toast.error("Preencha o reCaptcha.");
      return;
    } else {
      setLoad(true);
      try {
        const response: CheckResponse = await CheckLegalApi.getCheckOccurrences(
          {
            cmc7: MaskedInput.unmask(cmc7),
            checkSuid: MaskedInput.unmask(checkSuid),
            suid: MaskedInput.unmask(userSuid),
            recaptcha: token,
          }
        );
        setCheck(response);
        setToken(undefined);
      } catch {
        toast.error("Falha na consulta. Tente novamente em instantes");
      }
      setLoad(false);
    }
  };

  return (
    <div>
      <Loader show={load || isRecaptchaLoading} fullScreen />
      <Header />

      <div className="description-section">
        <div className="description-text-section">
          <h1 className="description-title"> Consulta de cheques Sicredi </h1>
          <p className="description-text">
            Aqui você pode consultar os cheques Sicredi antes de recebê-los como
            pagamento.
          </p>
          <p className="description-text">
            Consulte se o cheque está sem ocorrências:{" "}
            <br className="description-mobile-break" />
            sustado/revogado, furtado/roubado, e/ou cancelado, até o momento da
            consulta.
          </p>
        </div>
        <div className="description-image-section">
          <img
            data-testid="logo"
            src={DescriptionImage}
            alt="Pessoa utilizando notebook"
          />
        </div>
      </div>
      <form className="form-section">
        <div className="form">
          <Spacing appearance="large" />
          <h2 className="form-title"> Com o cheque em mãos, informe: </h2>
          <MaskedInput
            data-testid="checkSuid-field"
            mask="CPF_CNPJ"
            label="CPF/CNPJ do emitente do cheque"
            hintMessage={!check && "Informações impressas na folha de cheque"}
            required
            errorMessage={errors.checkSuid}
            value={checkSuid}
            onChange={(e) => setCheckSuid(e.target.value)}
            disabled={!!check}
          />
          <Spacing appearance="large" />
          <MaskedInput
            data-testid="cmc7-field"
            mask={CMC7_MASK}
            label="CMC7 da folha de cheque"
            hintMessage={
              !check &&
              "Este número se encontra na parte inferior da folha e tem 30 dígitos"
            }
            required
            errorMessage={errors.cmcSeven}
            maxLength={33}
            value={cmc7}
            onChange={(e) => setCmc7(e.target.value)}
            disabled={!!check}
          />
          <Spacing appearance="large" />

          <MaskedInput
            data-testid="userSuid-field"
            errorMessage={errors.userSuid}
            mask="CPF_CNPJ"
            label="Seu CPF/CNPJ"
            hintMessage={!check && "Dados de quem está consultando"}
            required
            value={userSuid}
            onChange={(e) => setUserSuid(e.target.value)}
            disabled={!!check}
          />
          <Spacing appearance="x-large" />

          {check && (
            <ResponseSection
              className="response-section-mobile"
              hasOccurrence={check.occurrences}
              date={check.date}
              protocol={check.protocol}
            />
          )}

          <div className={check && "mobile-form-undisplay"}>
            <div
              data-testid="mobile-check-image-container"
              className="form-mobile-image-section"
            >
              <img
                data-testid="mobile-check-image"
                src={CheckMobile}
                alt="Imagem cheque"
              />
            </div>

            <Checkbox
              data-testid="terms-checkbox"
              label="Declaro estar ciente de que as informações desta consulta são confidenciais e de uso exclusivo do interessado, não sendo permitida sua publicação."
              onChange={() => setTerms((prev) => !prev)}
              errorMessage={!terms && errors.terms}
              checked={terms}
              className={`${check && "undisplay-with-check"}`}
            />
            <Spacing
              appearance="x-large"
              className={`${check && "undisplay-with-check"}`}
            />

            <div
              id="g-recaptcha"
              className={`${check && "undisplay-with-check"}`}
            />
          </div>

          <Spacing appearance="x-large" />

          {check ? (
            <Button
              children="Fazer nova consulta"
              block
              appearance="primary"
              onClick={() => {
                setCheck(undefined);
                window.grecaptcha.enterprise.reset();
                clearFields();
              }}
            />
          ) : (
            <div className="buttons-container">
              <Button
                className="button mobile-clean"
                appearance="primary"
                ghost
                children="Limpar"
                onClick={clearFields}
              />
              <Button
                data-testid="desktop-clean-button"
                className="button desktop-clean"
                appearance="primary"
                ghost
                children="Limpar campos"
                onClick={clearFields}
              />
              <Button
                data-testid="find-check-button"
                className="button"
                appearance="primary"
                onClick={() => {
                  getCheckInformation();
                }}
                children="Consultar"
              />
            </div>
          )}
        </div>
        {check ? (
          <ResponseSection
            testId="desktop-response-section"
            className="desktop-response-section"
            hasOccurrence={check.occurrences}
            date={check.date}
            protocol={check.protocol}
          />
        ) : (
          <div
            data-testid="desktop-check-image-container"
            className="form-image-section"
          >
            <img
              data-testid="desktop-check-image"
              src={Check}
              alt="Imagem cheque"
            />
          </div>
        )}
      </form>
      <Footer />
    </div>
  );
};

export default Home;
